import { useState } from "react";
import axios from "axios";
import { Mail, Phone, Globe, MapPin, Send, Loader2, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SERVICES } from "../lib/services";
import { API_BASE } from "../lib/api";
import { Seo } from "../components/Seo";
import { PAGE_SEO, AREAS_SERVED } from "../lib/seo";
import { toast, Toaster } from "sonner";

const API = API_BASE;

const initial = { name: "", email: "", phone: "", company: "", service: "General Enquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-36 pb-8" data-testid="contact-page">
      <Seo path="/contact" title={PAGE_SEO["/contact"].title} description={PAGE_SEO["/contact"].description} />
      <Toaster richColors position="top-center" />

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="kb-label mb-6">Get in Touch</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] max-w-4xl">
            Let's build your <br /><span className="italic text-[#d4a840]">compliance roadmap.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Share a few details and our team will reach out within 24 hours with a bespoke plan
            — no obligations, no fine print.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="kb-glass rounded-2xl p-8">
              <div className="kb-label mb-6">Reach us directly</div>
              <ul className="space-y-6">
                <li className="flex items-start gap-4" data-testid="contact-info-email">
                  <div className="w-11 h-11 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-[#d4a840]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">Email</div>
                    <a href="mailto:info@kaveribiz.com" className="text-white text-lg hover:text-[#d4a840] transition-colors">info@kaveribiz.com</a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-phone">
                  <div className="w-11 h-11 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center shrink-0">
                    <Phone size={18} className="text-[#d4a840]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">Phone</div>
                    <a href="tel:+918867192221" className="text-white text-lg hover:text-[#d4a840] transition-colors">+91 88671 92221</a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-web">
                  <div className="w-11 h-11 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center shrink-0">
                    <Globe size={18} className="text-[#d4a840]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">Website</div>
                    <div className="text-white text-lg">www.kaveribiz.com</div>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-location">
                  <div className="w-11 h-11 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-[#d4a840]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">Serving</div>
                    <div className="text-white text-lg">Bangalore, Karnataka — and nationwide advisory</div>
                  </div>
                </li>
              </ul>

              <div className="mt-8 pt-8 border-t border-[#d4a840]/15">
                <div className="kb-label mb-3">Compliance Today. Success Tomorrow.</div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Your data is confidential. We never share enquiries with third parties.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="kb-glass rounded-2xl p-8 mt-6" data-testid="areas-served">
              <div className="kb-label mb-4">Areas We Serve</div>
              <div className="flex flex-wrap gap-2">
                {AREAS_SERVED.map((area) => (
                  <span key={area} className="text-xs text-white/70 border border-white/10 rounded-full px-3 py-1.5">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} className="kb-glass rounded-2xl p-8" data-testid="contact-form">
              <div className="kb-label mb-6">Enquiry form</div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} required minLength={2} testid="field-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="field-email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required minLength={6} testid="field-phone" />
                <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} testid="field-company" />

                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Service of interest</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    data-testid="field-service"
                    className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3.5 text-white focus:border-[#d4a840] focus:outline-none transition-colors"
                  >
                    <option>General Enquiry</option>
                    {SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">How can we help?</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={5}
                    rows={5}
                    data-testid="field-message"
                    className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3.5 text-white focus:border-[#d4a840] focus:outline-none transition-colors resize-none"
                    placeholder="Briefly describe your business or the compliance challenge you're facing..."
                  />
                </div>
              </div>

              <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
                <div className="text-xs text-white/50">We respond within 24 business hours.</div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="kb-btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> :
                   submitted ? <><Check size={18} /> Send another</> :
                   <>Send enquiry <Send size={16} /></>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, minLength, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        data-testid={testid}
        className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3.5 text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
      />
    </div>
  );
}
